import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LANGUAGES, resources, DEFAULT_LANGUAGE } from './languages';

/**
 * i18next initialisation for the whole app.
 *
 * - English + Telugu shipped today; more languages plug in via ./languages.js
 *   with zero component changes.
 * - Selected language is persisted in localStorage and restored on load, so it
 *   survives page refreshes and new sessions.
 * - Changing the language updates the entire UI instantly (react-i18next
 *   re-renders subscribed components) — no page reload needed.
 * - Only static UI text is translated. Database / API values are never keyed.
 */

export const STORAGE_KEY = 'app.language';

function detectInitialLanguage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LANGUAGES.some((l) => l.code === saved)) return saved;
  } catch (_) {
    /* localStorage unavailable — fall back to default */
  }
  return DEFAULT_LANGUAGE;
}

i18n.use(initReactI18next).init({
  resources,
  lng: detectInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: LANGUAGES.map((l) => l.code),
  defaultNS: 'translation',
  interpolation: {
    escapeValue: false, // React already escapes
  },
  returnEmptyString: false, // fall back to English if a key is blank
  react: {
    useSuspense: false, // resources are bundled, no async load / no flash
  },
});

// Persist selection + keep <html lang> in sync for a11y whenever it changes.
i18n.on('languageChanged', (lng) => {
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch (_) {
    /* ignore */
  }
  try {
    document.documentElement.setAttribute('lang', lng);
  } catch (_) {
    /* ignore (SSR / no document) */
  }
});

try {
  document.documentElement.setAttribute('lang', i18n.language || DEFAULT_LANGUAGE);
} catch (_) {
  /* ignore */
}

export default i18n;
